// src/components/Login.js - Updated with Firebase Integration
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../FirebaseContext';
import { 
  Shield, 
  Building, 
  Loader, 
  AlertCircle, 
  Server, 
  Info,
  CheckCircle,
  Globe,
  Lock,
  Users,
  BarChart3,
  FileText,
  Calendar,
  Settings,
  X,
  Eye,
  EyeOff,
  User,
  Mail,
  Phone,
  MapPin,
  Cloud,
  CloudOff,
  Wifi,
  WifiOff
} from 'lucide-react';

// ===========================================
// COMPANY REGISTRATION COMPONENT
// ===========================================
const CompanyRegistration = ({ onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    adminUsername: '',
    adminPassword: '',
    adminEmail: '',
    industry: '',
    phoneNumber: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const { registerCompany } = useAuth();
  const firebase = useFirebase();

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.adminUsername.trim()) {
      setError('Admin username is required');
      return false;
    }
    if (!formData.adminPassword || formData.adminPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (firebase.isConnected && (!formData.adminEmail || !formData.adminEmail.includes('@'))) {
      setError('Valid email is required for Firebase registration');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      console.log('🏢 Starting company registration...');
      
      const result = await registerCompany(formData, formData);
      
      if (result.success) {
        const method = result.method;
        const syncMessage = method === 'firebase' 
          ? 'Your data will sync across all your devices!' 
          : 'Data stored locally on this device only.';
        
        const features = [
          '✅ Complete data isolation',
          '✅ Bike management system', 
          '✅ User management (up to 10 users)',
          '✅ Financial tools & invoicing',
          '✅ Document management',
          '✅ Analytics & reporting',
          '✅ 30-day trial period'
        ];

        alert(
          `🎉 Company "${formData.companyName}" registered successfully!\n\n` +
          `🔐 Admin Credentials:\n` +
          `Username: ${formData.adminUsername}\n` +
          `Email: ${formData.adminEmail}\n\n` +
          `🌐 Registration Method: ${method.toUpperCase()}\n` +
          `${syncMessage}\n\n` +
          `🚀 Features Enabled:\n` +
          `${features.join('\n')}\n\n` +
          `You can now login with these credentials.`
        );
        
        // Reset form
        setFormData({
          companyName: '',
          adminUsername: '',
          adminPassword: '',
          adminEmail: '',
          industry: '',
          phoneNumber: '',
          address: ''
        });
        
        onSuccess?.();
        onBack();
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600 mb-2">
            Register Your Company
          </h1>
          <p className="text-gray-400">Join the Multi-Tenant Bike Manager Platform</p>
          
          {/* Firebase Status Indicator */}
          <div className="flex items-center justify-center mt-3 space-x-4">
            <div className={`flex items-center text-sm px-3 py-1 rounded-full ${
              firebase.isConnected 
                ? 'bg-green-900 text-green-300 border border-green-700' 
                : 'bg-yellow-900 text-yellow-300 border border-yellow-700'
            }`}>
              {firebase.isConnected ? (
                <>
                  <Cloud className="mr-1" size={14} />
                  Firebase Connected
                </>
              ) : (
                <>
                  <CloudOff className="mr-1" size={14} />
                  Offline Mode
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Building className="mr-2" size={20} />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Building className="inline mr-1" size={16} />
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter company name"
                    disabled={loading}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Settings className="inline mr-1" size={16} />
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    disabled={loading}
                  >
                    <option value="">Select industry</option>
                    <option value="bike-rental">🚴 Bike Rental</option>
                    <option value="bike-shop">🛠️ Bike Shop</option>
                    <option value="bike-tours">🗺️ Bike Tours</option>
                    <option value="bike-maintenance">🔧 Bike Maintenance</option>
                    <option value="bike-sharing">🔄 Bike Sharing</option>
                    <option value="logistics">📦 Delivery & Logistics</option>
                    <option value="other">🏢 Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Phone className="inline mr-1" size={16} />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter phone number"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <MapPin className="inline mr-1" size={16} />
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter address"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Administrator Information */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 pt-4 border-t border-gray-600 flex items-center">
                <Shield className="mr-2" size={20} />
                Company Administrator
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <User className="inline mr-1" size={16} />
                    Admin Username *
                  </label>
                  <input
                    type="text"
                    value={formData.adminUsername}
                    onChange={(e) => setFormData({ ...formData, adminUsername: e.target.value })}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter admin username"
                    disabled={loading}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Lock className="inline mr-1" size={16} />
                    Admin Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.adminPassword}
                      onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                      className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 pr-12"
                      placeholder="Enter password (min 6 chars)"
                      disabled={loading}
                      required
                      minLength="6"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Mail className="inline mr-1" size={16} />
                    Admin Email {firebase.isConnected && '*'}
                  </label>
                  <input
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter admin email"
                    disabled={loading}
                    required={firebase.isConnected}
                  />
                  {firebase.isConnected && (
                    <p className="text-xs text-blue-400 mt-1">Required for Firebase sync</p>
                  )}
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-900 border border-red-700 rounded-lg p-3 flex items-center">
                <AlertCircle className="text-red-400 mr-2 flex-shrink-0" size={20} />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin mr-2" size={20} />
                    Registering...
                  </>
                ) : (
                  <>
                    <Building className="mr-2" size={20} />
                    Register Company
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onBack}
                disabled={loading}
                className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
              >
                <X className="mr-2" size={16} />
                Back
              </button>
            </div>
          </form>

          {/* Features Information */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="bg-gradient-to-r from-blue-900 to-purple-900 border border-blue-700 rounded-lg p-4">
              <h4 className="text-blue-300 font-medium mb-3 flex items-center">
                {firebase.isConnected ? (
                  <>
                    <Cloud className="mr-2" size={20} />
                    Firebase Cloud Features
                  </>
                ) : (
                  <>
                    <Server className="mr-2" size={20} />
                    Local Storage Features
                  </>
                )}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-200 text-sm">
                <ul className="space-y-1">
                  <li className="flex items-center">
                    <Lock className="mr-2" size={12} />
                    Complete data isolation
                  </li>
                  <li className="flex items-center">
                    <Users className="mr-2" size={12} />
                    Up to 10 users per company
                  </li>
                  <li className="flex items-center">
                    <Settings className="mr-2" size={12} />
                    Document fields management
                  </li>
                  <li className="flex items-center">
                    <Building className="mr-2" size={12} />
                    Bike inventory system
                  </li>
                </ul>
                <ul className="space-y-1">
                  <li className="flex items-center">
                    <FileText className="mr-2" size={12} />
                    Invoice & quotation tools
                  </li>
                  <li className="flex items-center">
                    <Calendar className="mr-2" size={12} />
                    Booking management
                  </li>
                  <li className="flex items-center">
                    <BarChart3 className="mr-2" size={12} />
                    Analytics & reporting
                  </li>
                  <li className="flex items-center">
                    {firebase.isConnected ? (
                      <>
                        <Wifi className="mr-2" size={12} />
                        Cross-device sync
                      </>
                    ) : (
                      <>
                        <WifiOff className="mr-2" size={12} />
                        Local storage only
                      </>
                    )}
                  </li>
                </ul>
              </div>
              
              {!firebase.isConnected && (
                <div className="mt-3 p-2 bg-yellow-900 border border-yellow-700 rounded text-yellow-200 text-xs">
                  <Info className="inline mr-1" size={12} />
                  Firebase offline: Data will be stored locally and won't sync across devices.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// MAIN LOGIN COMPONENT
// ===========================================
const Login = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const { login, resetPassword, firebaseConnected } = useAuth();
  const firebase = useFirebase();

  // Show registration component
  if (showRegistration) {
    return (
      <CompanyRegistration 
        onBack={() => setShowRegistration(false)}
        onSuccess={() => {
          console.log('✅ Registration completed successfully');
        }}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username.trim() || !credentials.password.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(credentials.username.trim(), credentials.password);
      console.log('✅ Login successful:', result.method);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = prompt('Enter your email address for password reset:');
    if (!email) return;

    try {
      setLoading(true);
      await resetPassword(email);
      alert('Password reset email sent! Check your inbox.');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600 mb-2">
            Bike Manager
          </h1>
          <p className="text-gray-400">Multi-Tenant Business Management Platform</p>
          
          {/* Connection Status */}
          <div className="flex items-center justify-center mt-3 space-x-4 text-sm">
            <div className={`flex items-center px-2 py-1 rounded ${
              firebase.isConnected 
                ? 'bg-green-900 text-green-400' 
                : 'bg-gray-700 text-gray-400'
            }`}>
              {firebase.isConnected ? (
                <>
                  <Cloud size={14} className="mr-1" />
                  Cloud Sync
                </>
              ) : (
                <>
                  <CloudOff size={14} className="mr-1" />
                  Offline
                </>
              )}
            </div>
            <div className="flex items-center text-blue-400">
              <Server className="mr-1" size={14} />
              Multi-Tenant
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <User className="inline mr-1" size={16} />
                Username or Email
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter username or email"
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Lock className="inline mr-1" size={16} />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-900 border border-red-700 rounded-lg p-3 flex items-center">
                <AlertCircle className="text-red-400 mr-2" size={20} />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin mr-2" size={20} />
                  Signing in...
                </>
              ) : (
                <>
                  <Shield className="mr-2" size={20} />
                  Sign In
                </>
              )}
            </button>

            {/* Forgot Password */}
            {firebase.isConnected && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </form>

          {/* Register New Company */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <button
              onClick={() => setShowRegistration(true)}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Building size={16} />
              <span>Register New Company</span>
            </button>
          </div>

          {/* Demo Info */}
          <div className="mt-4 p-3 bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg">
            <h4 className="text-gray-300 text-sm font-medium mb-2 flex items-center">
              <Info className="mr-2" size={16} />
              Quick Access
            </h4>
            <div className="text-gray-400 text-xs space-y-1">
              <div className="flex items-center">
                <Building className="mr-2" size={12} />
                <strong>Demo:</strong> admin / admin123
              </div>
              <div className="flex items-center">
                <Users className="mr-2" size={12} />
                <strong>New Company:</strong> Click "Register New Company"
              </div>
              <div className="flex items-center">
                {firebase.isConnected ? (
                  <>
                    <Cloud className="mr-2" size={12} />
                    <strong>Sync:</strong> Data syncs across devices
                  </>
                ) : (
                  <>
                    <CloudOff className="mr-2" size={12} />
                    <strong>Local:</strong> Data stored locally only
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;